import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { ErrorCodeEnum } from '@ulysses-inc/harami_api_client'
import { type FC, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Prompt, useHistory } from 'react-router-dom'
import { Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import type { ErrorCodeErrorType } from 'src/features/firebase/featureSpecificEventLog/gridLayoutSoftLimitError'
import {
  clearPatchGridVariablesError,
  requestUpdateGridVariablesTable,
} from 'src/state/ducks/editGridVariables/actions'
import type { SoftLimitErrorCode } from 'src/state/ducks/editGridVariables/reducers'
import {
  selectErrorRowNumbers,
  selectHasChangedInTable,
  selectTemplateName,
} from 'src/state/ducks/editGridVariables/selectors'
import type { RootState } from 'src/state/store'
import type { HistoryState } from 'src/views/components/routes/getUserConfirmation'
import { useSoftLimitErrorLog } from './useSoftLimitErrorLog'

type OwnProps = {
  templateId: number
}

export const PageHeader: FC<OwnProps> = ({ templateId }) => {
  return useMemo(
    () => <UnmemorizedPageHeader templateId={templateId} />,
    [templateId],
  )
}

const toSoftLimitErrorType = (code: SoftLimitErrorCode): ErrorCodeErrorType => {
  switch (code) {
    case ErrorCodeEnum.MaxSubstantialCellCountExceededInGridVariablesError:
      return 'cellCount'
    case ErrorCodeEnum.MaxRowCountExceededInGridVariablesError:
      return 'rowCount'
    case ErrorCodeEnum.MaxTableCountExceededInGridVariablesError:
      return 'tableCount'
  }
}

const UnmemorizedPageHeader: FC<OwnProps> = ({ templateId }) => {
  const dispatch = useDispatch()
  const history = useHistory()

  const [historyState] = useState<HistoryState>(() => ({
    data: window.history.state,
    title: document.title,
    url: window.location.href,
  }))

  const templateName = useSelector(selectTemplateName)
  const isChangedInTable = useSelector(selectHasChangedInTable)
  const patchGridVariablesError = useSelector(
    (state: RootState) =>
      state.editGridVariablesState.editGridVariables.patchGridVariablesError,
  )

  const { logSoftLimitErrorEvent } = useSoftLimitErrorLog()

  // NOTE: errorRowNumbersについて
  // レンダリングのたびにエラーになった行数を数えているので、要パフォーマンスチューニング。
  // 暫定対応でモーダルに表示している。
  const errorRowNumbers = useSelector(selectErrorRowNumbers)

  // バリデーションエラー表示用のフラグ（ユーザーが保存ボタンを押したかどうか）
  const [hasTriedToSave, setHasTriedToSave] = useState(false)

  // エラーダイアログの内容を patchGridVariablesError と hasTriedToSave から導出
  const validationError =
    hasTriedToSave && errorRowNumbers.length > 0
      ? {
          title: '保存できませんでした',
          message: `${errorRowNumbers.length}行のデータを修正して再度保存してください。`,
        }
      : null

  const apiError = patchGridVariablesError
    ? {
        title: '取り込み項目が保存できませんでした',
        message: patchGridVariablesError.errorMessage,
      }
    : null

  // どちらかのエラーがあればダイアログを表示（バリデーションエラーを優先）
  const errorDialog = validationError || apiError
  const isErrorDialogOpen = !!errorDialog

  // APIエラー発生時のログ送信
  useEffect(() => {
    if (patchGridVariablesError) {
      logSoftLimitErrorEvent(
        toSoftLimitErrorType(patchGridVariablesError.code),
        patchGridVariablesError.errorMessage,
      )
    }
  }, [patchGridVariablesError, logSoftLimitErrorEvent])

  const handleCloseErrorDialog = () => {
    setHasTriedToSave(false)
    dispatch(clearPatchGridVariablesError())
  }

  const handleClickCancel = () => {
    history.push('/gridVariables')
  }

  const handleClickSave = () => {
    if (errorRowNumbers.length > 0) {
      setHasTriedToSave(true)
      return
    }

    setHasTriedToSave(false)
    dispatch(requestUpdateGridVariablesTable(templateId))
  }

  return (
    <>
      <Prompt
        when={isChangedInTable}
        message={location => {
          const message = '変更した内容が破棄されますがよろしいですか？'
          if (location.pathname === `/gridVariables/${templateId}/edit`) {
            return JSON.stringify({ message, state: historyState })
          }
          return message
        }}
      />

      <Header
        rightSlot={[
          <Button
            key="1"
            variant="contained"
            color="primary"
            onClick={handleClickSave}
            disabled={!isChangedInTable}
          >
            保存
          </Button>,
        ]}
        sticky
      >
        <HeaderBackButtonMui
          onClick={handleClickCancel}
          style={{ fontWeight: 'bold', fontSize: '15px' }}
        />
        <HeaderTitle title={templateName} />
      </Header>

      <Dialog
        open={isErrorDialogOpen}
        onClose={handleCloseErrorDialog}
        maxWidth="sm"
      >
        <DialogTitle>{errorDialog?.title}</DialogTitle>
        <DialogContent>{errorDialog?.message}</DialogContent>
        <DialogActions>
          <Button onClick={handleCloseErrorDialog} variant="outlined">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
